import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const toPayload = (f) => ({ question: f.question || "", answer: f.answer || "", sort_order: Number(f.sort_order) || 0, active: f.active !== false });

export default function FAQManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", sort_order: 0 });

  const load = useCallback(() => http.get("/admin/faqs").then((r) => setItems(r.data.faqs)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.question.trim() || !form.answer.trim()) return toast.error("Question and answer are required");
    try {
      await http.post("/admin/faqs", { ...form, sort_order: Number(form.sort_order) || 0 });
      toast.success("FAQ added");
      setForm({ question: "", answer: "", sort_order: 0 });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const save = async (f, patch) => {
    try {
      await http.put(`/admin/faqs/${f.id}`, { ...toPayload(f), ...patch });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const edit = (f) => {
    const question = window.prompt("Edit question", f.question);
    if (question === null) return;
    const answer = window.prompt("Edit answer", f.answer);
    if (answer === null) return;
    save(f, { question: question || f.question, answer: answer || f.answer });
  };

  return (
    <div className="space-y-6" data-testid="faq-manager">
      <div className="card">
        <h3 className="font-serif text-2xl mb-4">Add FAQ</h3>
        <div className="grid gap-3">
          <input data-testid="faq-form-question" placeholder="Question *" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
          <textarea data-testid="faq-form-answer" rows="2" placeholder="Answer *" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
          <div className="flex gap-3 items-center">
            <input data-testid="faq-form-order" type="number" placeholder="Order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} style={{ width: 100 }} />
            <button data-testid="faq-form-add" onClick={add} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add FAQ</button>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="font-serif text-2xl mb-3">FAQs ({items.filter((i) => i.active).length} shown on website)</h3>
        <div className="space-y-2">
          {items.map((f) => (
            <div key={f.id} className="flex flex-wrap items-start gap-2 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`faq-row-${f.id}`}>
              <div className="flex-1 min-w-40">
                <div className="font-medium">{f.question}{!f.active && <span className="badge-terracotta ml-2">hidden</span>}</div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>{f.answer}</div>
              </div>
              <input data-testid={`faq-order-${f.id}`} type="number" defaultValue={f.sort_order} key={`o-${f.id}-${f.sort_order}`}
                onBlur={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v !== f.sort_order) save(f, { sort_order: v }); }}
                style={{ width: 70, padding: "6px 10px" }} title="Display order" />
              <button data-testid={`faq-edit-${f.id}`} onClick={() => edit(f)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>Edit</button>
              <button data-testid={`faq-toggle-${f.id}`} onClick={() => save(f, { active: !f.active })} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>
                {f.active ? "Hide" : "Show"}
              </button>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No FAQs yet.</p>}
        </div>
      </div>
    </div>
  );
}
